import { Component, Inject, inject, Injectable, Input, OnDestroy, OnInit } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, from, lastValueFrom, throwError, of, Subscription, Subject, combineLatest } from 'rxjs';
import { catchError, switchMap, tap, takeUntil, share, take } from 'rxjs/operators';
import { config } from '../../config/appConfig';
import { HttpService } from './http.service';
import { Thumbmark } from "@thumbmarkjs/thumbmarkjs";
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Actions, ofType } from '@ngrx/effects';
import { GetSettingAction, SessionRefreshAction, SessionRefreshFailureAction, SessionRefreshSuccessAction, SessionRemoveDevicesAction, SessionRemoveDevicesFailureAction, SessionRemoveDevicesSuccessAction, SessionRevokeAction, SessionRevokeFailureAction, SessionRevokeSuccessAction } from '../store/user/user.actions';
import { Store } from '@ngrx/store';
import { ICreateSessionRequest, ISessionResponse } from '../models/user';
import { SnackBarService } from './snackbar.service';
import { UserService } from './user.service';
import { LogoutAction } from '../store/auth/auth.actions';


@Injectable({
    providedIn: 'root',
})
export class SessionService implements OnDestroy {
    private apiUrl = {
        initiateSession: '/session',
    };
    private sessionToken: string | null = null;
    private sessionExpiry: number | null = null;
    private refreshTimeout: any;
    private isInitialized: boolean = false;
    private httpService = new HttpService(config.userServiceURL);

    private _dialog = inject(MatDialog);
    private browserLimitDialogRef?: MatDialogRef<BrowserLimitDialogComponent>;
    private screenLimitDialogRef?: MatDialogRef<ScreenLimitDialogComponent>;
    private browserAndScreenLimitDialogRef?: MatDialogRef<BrowserAndScreenLimitDialogComponent>;

    // Add properties for initialization tracking
    private sessionInitSubject = new Subject<ISessionResponse>();
    private sessionInitObservable: Observable<ISessionResponse> | null = null;
    private isInitializing: boolean = false;
    private destroy$ = new Subject<void>();

    constructor(private userService: UserService, private actions$: Actions, private store: Store) {
        // Handle Session Refresh Success
        this.actions$.pipe(
            ofType(SessionRefreshSuccessAction),
            takeUntil(this.destroy$)
        ).subscribe(({ response }) => {
            this.sessionToken = response.Value.SessionToken;
            this.sessionExpiry = new Date(response.Value.ExpiresAt).getTime();
            this.scheduleRefresh(response.Value.SessionToken, this.sessionExpiry);
        });

        // Handle Session Refresh Failure
        this.actions$.pipe(
            ofType(SessionRefreshFailureAction),
            takeUntil(this.destroy$)
        ).subscribe(({ error }) => {
            // If Refreshing the Session Fails, then reinitiaate the session.
            this.initiateSession().subscribe({
                error: err => console.error('Failed to initiate new session:', err)
            });
        });
    }

    private async getDeviceUUID(): Promise<string> {
        const tm = new Thumbmark();
        const result = await tm.get();
        return result.thumbmark;
    }

    private getOSInfo(): string {
        const platform = navigator.platform.toLowerCase();
        const ua = navigator.userAgent.toLowerCase();

        if (platform.includes('win')) return 'Windows';
        if (platform.includes('mac')) return 'macOS';
        if (platform.includes('linux')) return 'Linux';
        if (ua.includes('android')) return 'Android';
        if (ua.includes('iphone') || ua.includes('ipad') || ua.includes('ipod')) return 'iOS';

        return 'Unknown';
    }

    private getBrowserInfo(): string {
        const ua = navigator.userAgent.toLowerCase();
        let name = 'Unknown', version = 'Unknown';

        if (ua.includes('edg')) {
            name = 'Edge';
            version = ua.match(/edg\/([\d.]+)/)?.[1] || 'Unknown';
        } else if (ua.includes('chrome') || ua.includes('crios')) {
            name = 'Chrome';
            version = ua.match(/(chrome|crios)\/([\d.]+)/)?.[2] || 'Unknown';
        } else if (ua.includes('firefox') || ua.includes('fxios')) {
            name = 'Firefox';
            version = ua.match(/(firefox|fxios)\/([\d.]+)/)?.[2] || 'Unknown';
        } else if (ua.includes('safari') && !ua.includes('chrome') && !ua.includes('crios') && !ua.includes('edg')) {
            name = 'Safari';
            version = ua.match(/version\/([\d.]+)/)?.[1] || 'Unknown';
        } else if (ua.includes('opr') || ua.includes('opera')) {
            name = 'Opera';
            version = ua.match(/(opera|opr)\/([\d.]+)/)?.[2] || 'Unknown';
        } else if (ua.includes('msie') || ua.includes('trident')) {
            name = 'Internet Explorer';
            version = ua.match(/(msie |rv:)([\d.]+)/)?.[2] || 'Unknown';
        }

        return `${name} ${version}`;
    }

    async getDeviceInfo(): Promise<ICreateSessionRequest> {
        const fingerPrintId = await this.getDeviceUUID();
        return {
            Name: this.getBrowserInfo(),
            Fingerprint: fingerPrintId,
            Type: "Website",
            OS: this.getOSInfo()
        };
    }

    // Check If the Session Token Is Valid
    private isSessionTokenValid(): boolean {
        if (!this.sessionToken || !this.sessionExpiry) {
            return false;
        }
        const currentTime = Date.now();
        return this.sessionExpiry - currentTime > 2 * 60 * 1000;    // expires in more than 2 minutes to account for refresh buffer
    }

    initiateSession(): Observable<ISessionResponse> {
        // If session is being initialized, return the existing observable
        if (this.isInitializing && this.sessionInitObservable) {
            return this.sessionInitObservable;
        }
        this.isInitializing = true;
        this.sessionInitObservable = from(this.getDeviceInfo()).pipe(
            switchMap(deviceInfo =>
                this.httpService.post(this.apiUrl.initiateSession, deviceInfo).pipe(
                    switchMap(response => of({
                        SessionToken: response.Value.SessionToken,
                        ExpiresAt: response.Value.ExpiresAt,
                    }))
                )
            ),
            tap(response => {
                this.sessionToken = response.SessionToken;
                this.sessionExpiry = new Date(response.ExpiresAt).getTime();
                if (this.sessionToken && this.sessionExpiry) {
                    this.scheduleRefresh(this.sessionToken, this.sessionExpiry);
                }
                this.isInitialized = true;
                if (this.browserLimitDialogRef) {
                    this.browserLimitDialogRef.close();
                }
                if (this.screenLimitDialogRef) {
                    this.screenLimitDialogRef.close();
                }
                if (this.browserAndScreenLimitDialogRef) {
                    this.browserAndScreenLimitDialogRef.close();
                }
                this.isInitializing = false;
                this.sessionInitSubject.next(response);
                this.sessionInitSubject.complete();
                this.sessionInitObservable = null;
                this.sessionInitSubject = new Subject<ISessionResponse>();
            }),
            catchError(error => {
                this.isInitializing = false;
                this.sessionInitSubject.error(error);
                this.sessionInitObservable = null;
                this.sessionInitSubject = new Subject<ISessionResponse>();
                return this.handleError(error);
            }),
            share()
        );
        return this.sessionInitObservable;
    }

    private scheduleRefresh(sessionToken: string, expiryTime: number) {
        if (this.refreshTimeout) clearTimeout(this.refreshTimeout);
        const refreshTime = expiryTime - Date.now() - 2 * 60 * 1000;  // Refresh 2 minutes before the expiry
        if (refreshTime > 0) {
            this.refreshTimeout = setTimeout(() => this.refreshSession(sessionToken), refreshTime);
        }
    }

    refreshSession(sessionToken: string): Observable<ISessionResponse> {
        this.store.dispatch(SessionRefreshAction({ request: { SessionToken: sessionToken } }));
        return this.actions$.pipe(
            ofType(SessionRefreshSuccessAction, SessionRefreshFailureAction),
            takeUntil(this.destroy$),
            switchMap(action => {
                if (action.type === SessionRefreshSuccessAction.type) {
                    return of(action.response.Value);
                }
                return throwError(() => new Error('Session refresh failed'));
            })
        );
    }

    getSessionToken(): string | null {
        return this.sessionToken;
    }

    async initializeApp(): Promise<void> {
        try {
            await lastValueFrom(this.initiateSession());
            this.store.dispatch(GetSettingAction());
        } catch (error: any) {
            this.handleError(error as HttpErrorResponse);
            throw error;
        }
    }

    isSessionInitialized(): boolean {
        return this.isInitialized;
    }

    decodeJWT(token: string) {
        if (!token) return null;

        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }

        try {
            const payload = parts[1];
            const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
            return JSON.parse(decodeURIComponent(escape(decoded)));
        } catch (error) {
            return null;
        }
    }


    private handleError(error: HttpErrorResponse): Observable<never | ISessionResponse> {
        const errors = error.error?.Errors || [];
        const errorCodes = errors.map((err: any) => err.Code);
        const errorMessages = errors.map((err: any) => err.Message || 'Unknown error');

        if (errorCodes.includes('browser-limit-exhausted') && errorCodes.includes('screens-limit-exhausted')) {
            return this.handleBrowserAndScreenLimitExhaused('Maximum number of browsers and screens/devices reached.')
        } else if (errorCodes.includes('invalid-session-token')) {
            return this.handleInvalidSessionToken(errorMessages[errorCodes.indexOf('invalid-session-token')] || 'Invalid session token');
        } else if (errorCodes.includes('browser-limit-exhausted')) { // Remove Devices
            return this.handleBrowserLimitExhausted(errorMessages[errorCodes.indexOf('browser-limit-exhausted')] || 'Browser limit exhausted');
        } else if (errorCodes.includes('screens-limit-exhausted')) {   // Revoke Sesion 
            return this.handleScreensLimitExhausted(errorMessages[errorCodes.indexOf('screens-limit-exhausted')] || 'Screen limit exhausted');
        }
        return throwError(() => new Error('An error occurred: ' + (error.message || 'Unknown error')));
    }

    private handleInvalidSessionToken(errorMessage: string): Observable<ISessionResponse> {
        return this.initiateSession().pipe(
            catchError(err => throwError(() => new Error(`Failed to initiate new session: ${err.message || errorMessage}`)))
        );
    }

    // Handle The Case When Browser And Screen Limit Errors occur simultaneously
    private handleBrowserAndScreenLimitExhaused(errorMessage: string): Observable<never | ISessionResponse> {
        const tokenInfo = this.decodeJWT(localStorage.getItem("token") || "");
        const showRemoveDevicesOption = !!tokenInfo?.UserInfo?.IsSubscriptionOwner;
        const dialogConfig: MatDialogConfig = {
            width: '450px',
            maxWidth: '90vw',
            panelClass: 'browser-and-screen-limit-dialog',
            disableClose: true,
            data: { message: errorMessage, showRemoveDevicesOption }
        };
        this.browserAndScreenLimitDialogRef = this._dialog.open(BrowserAndScreenLimitDialogComponent, dialogConfig);
        return throwError(() => new Error('User declined to clear devices and sessions'));
    }

    private handleBrowserLimitExhausted(errorMessage: string): Observable<never | ISessionResponse> {
        const tokenInfo = this.decodeJWT(localStorage.getItem("token") || "");
        const showRemoveDevicesOption = !!tokenInfo?.UserInfo?.IsSubscriptionOwner;
        const dialogConfig: MatDialogConfig = {
            width: '450px',
            maxWidth: '90vw',
            panelClass: 'browser-limit-dialog',
            disableClose: true,
            data: { message: errorMessage, showRemoveDevicesOption }
        };
        this.browserLimitDialogRef = this._dialog.open(BrowserLimitDialogComponent, dialogConfig);
        return throwError(() => new Error('User declined to free browsers'));
    }


    private handleScreensLimitExhausted(errorMessage: string): Observable<never | ISessionResponse> {
        const tokenInfo = this.decodeJWT(localStorage.getItem("token") || "");
        const showRemoveDevicesOption = !!tokenInfo?.UserInfo?.IsSubscriptionOwner;
        const dialogConfig: MatDialogConfig = {
            width: '450px',
            maxWidth: '90vw',
            panelClass: 'browser-limit-dialog',
            disableClose: true,
            data: { message: errorMessage, showRemoveDevicesOption }
        };
        this.screenLimitDialogRef = this._dialog.open(ScreenLimitDialogComponent, dialogConfig);
        return throwError(() => new Error('User declined to deactivate sessions'));
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

@Component({
    selector: 'browser-limit-dialog',
    standalone: true,
    template: `
        <div class="dialog-content">
            <div class="message-container">
                <div class="d-flex justify-content-center align-items-center">
                    <div class="svg-container d-flex justify-content-center align-items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" 
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" 
                        stroke-linejoin="round" class="lucide lucide-triangle-alert h-12 w-12 text-amber-600" aria-hidden="true">
                            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path>
                            <path d="M12 9v4"></path><path d="M12 17h.01"></path>
                        </svg>
                    </div>
                </div>
                <div *ngIf="!showRemoveDevicesOption" class="secondary-message">
                    Please contact the subscription owner to remove active devices.
                </div>
                <div *ngIf="!showRemoveDevicesOption" class="d-flex justify-content-center align-items-center">
                    <button class="dialog-action" (click)="logout()">Close</button>
                </div>
                <div *ngIf="showRemoveDevicesOption" class="revoke-option">
                    <span>You've reached the maximum browser limit for your account. Remove an existing browser session to continue here.</span>
                </div>
                <div *ngIf="showRemoveDevicesOption" class="d-flex justify-content-center align-items-center">
                    <button [ngClass]="{ 'disabled-btn': isActionLoading }"  class="dialog-action" (click)="removeDevices()">{{ isActionLoading ? 'Loading...' : 'Continue Here'}}</button>
                </div>
            </div>
        </div>
    `,
    styles: [`
      :host {
          display: block;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
      }
      .svg-container {
        align-items: center;
        background-color: #fef3c6;
        border-radius: 50%;
        height: 70px;
        width: 70px;
      }
      .dialog-content {
          text-align: center;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 20px;
          border-radius: 8px;
          background-color: #ffffff;
          color: #333333;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
      }
      .message-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
      }
      .dialog-message {
          font-size: 18px;
          font-weight: 600;
          line-height: 1.4;
      }
      .secondary-message {
          font-size: 16px;
          font-weight: 400;
          color: #555555;
      }
      .revoke-option {
          display: flex;
          flex-direction: column;
          gap: 12px;
      }
      .revoke-option span {
          font-size: 16px;
          font-weight: 400;
          color: #555555;
      }
        .dialog-action {
          background-color: red;
          color: #ffffff;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 500;
          align-self: flex-start;
          transition: background-color 0.2s ease, transform 0.1s ease;
      }
      .dialog-action:hover {
        //   background-color: #0056b3;
          transform: translateY(-1px);
      }
      .dialog-action:active {
          transform: translateY(0);
      }
      .dialog-close {
          background: transparent;
          border: none;
          color: #333333;
          font-size: 20px;
          font-weight: 600;
          cursor: pointer;
          transition: color 0.2s ease, transform 0.1s ease;
      }
      .dialog-close:hover {
          color: #777777;
          transform: scale(1.1);
      }
      .dialog-close:active {
          transform: scale(1);
      }
      button.disabled-btn {
        cursor: not-allowed;
        opacity: 0.4;
      }
     
    `],
    imports: [CommonModule]
})
export class BrowserLimitDialogComponent implements OnInit {
    @Input() message: string = '';
    @Input() showRemoveDevicesOption: boolean = false;
    private _componentSubscriptions: Subscription[] = [];
    private destroy$ = new Subject<void>();
    isActionLoading: boolean = false;

    constructor(
        private snackbarService: SnackBarService,
        private dialogRef: MatDialogRef<BrowserLimitDialogComponent>, private actions$: Actions, private store: Store,
        @Inject(MAT_DIALOG_DATA) public data: { message: string, showRemoveDevicesOption: boolean }
    ) { }

    ngOnInit() {
        if (this.data) {
            this.message = this.data.message || '';
            this.showRemoveDevicesOption = this.data.showRemoveDevicesOption || false;
        }

        this._componentSubscriptions.push(
            this.actions$.pipe(
                ofType(SessionRemoveDevicesSuccessAction),
                takeUntil(this.destroy$)
            ).subscribe(({ response }) => {
                this.snackbarService.showSnackBar("success", response.Value);
                setTimeout(() => {
                    this.isActionLoading = false;
                    this.dialogRef.close();
                    location.reload();
                }, 1000);
            })
        );

        this._componentSubscriptions.push(
            this.actions$.pipe(
                ofType(SessionRemoveDevicesFailureAction),
                takeUntil(this.destroy$)
            ).subscribe(({ error }) => {
                this.isActionLoading = false;
                this.snackbarService.showSnackBar("error", "Removing Devices Failed.");
            })
        );
    }

    removeDevices() {
        this.isActionLoading = true;
        this.store.dispatch(SessionRemoveDevicesAction({
            request: {
                IsWeb: true,
                RemoveAll: true,
                DeviceIds: []
            }
        }))
    }

    logout() {
        this.store.dispatch(LogoutAction({ skipSessionLogout: true }));
        this.dialogRef.close();
    }

    closeDialog() {
        this.dialogRef.close();
    }
}


// Screen Limit Exhausted Component
@Component({
    selector: 'screen-limit-dialog',
    standalone: true,
    template: `
        <div class="dialog-content">
            <div class="message-container">
                <div class="d-flex justify-content-center align-items-center">
                    <div class="svg-container d-flex justify-content-center align-items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" 
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" 
                        stroke-linejoin="round" class="lucide lucide-triangle-alert h-12 w-12 text-amber-600" aria-hidden="true">
                            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path>
                            <path d="M12 9v4"></path><path d="M12 17h.01"></path>
                        </svg>
                    </div>
                </div>
         
                <div *ngIf="!showRemoveDevicesOption" class="secondary-message">
                    Please contact the subscription owner to revoke active sessions.
                </div>
                 <div *ngIf="!showRemoveDevicesOption" class="d-flex justify-content-center align-items-center">
                    <button class="dialog-action" (click)="logout()">Close</button>
                </div>

                <div *ngIf="showRemoveDevicesOption" class="revoke-option">
                    <span>Your account is already active on another screen. If you continue here, that session will end.</span>
                </div>
                <div *ngIf="showRemoveDevicesOption" class="d-flex justify-content-center align-items-center">
                    <button  [ngClass]="{ 'disabled-btn': isActionLoading }" class="dialog-action" (click)="revokeSessions()">{{isActionLoading ? 'Loading...' : 'Revoke All Sessions'}}</button>
                </div>
            </div>
        </div>
    `,
    styles: [`
      :host {
          display: block;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
      }
      .svg-container {
        align-items: center;
        background-color: #fef3c6;
        border-radius: 50%;
        height: 70px;
        width: 70px;
      }
      .dialog-content {
          text-align: center;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 20px;
          border-radius: 8px;
          background-color: #ffffff;
          color: #333333;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
      }
      .message-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
      }
      .dialog-message {
          font-size: 18px;
          font-weight: 600;
          line-height: 1.4;
      }
      .secondary-message {
          font-size: 16px;
          font-weight: 400;
          color: #555555;
      }
      .revoke-option {
          display: flex;
          flex-direction: column;
          gap: 12px;
      }
      .revoke-option span {
          font-size: 16px;
          font-weight: 400;
          color: #555555;
      }
      .dialog-action {
          background-color: red;
          color: #ffffff;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 500;
          align-self: flex-start;
          transition: background-color 0.2s ease, transform 0.1s ease;
      }
      
      .dialog-action:active {
          transform: translateY(0);
      }
      .dialog-close {
          background: transparent;
          border: none;
          color: #333333;
          font-size: 20px;
          font-weight: 600;
          cursor: pointer;
          transition: color 0.2s ease, transform 0.1s ease;
      }
      .dialog-close:hover {
          color: #777777;
          transform: scale(1.1);
      }
      .dialog-close:active {
          transform: scale(1);
      }
      button.disabled-btn {
        cursor: not-allowed;
        opacity: 0.4;
      }
    `],
    imports: [CommonModule]
})
export class ScreenLimitDialogComponent implements OnInit {
    @Input() message: string = '';
    @Input() showRemoveDevicesOption: boolean = false;
    private _componentSubscriptions: Subscription[] = [];
    private destroy$ = new Subject<void>();
    isActionLoading: boolean = false;

    constructor(
        private snackbarService: SnackBarService,
        private dialogRef: MatDialogRef<ScreenLimitDialogComponent>, private actions$: Actions, private store: Store,
        @Inject(MAT_DIALOG_DATA) public data: { message: string, showRemoveDevicesOption: boolean }
    ) { }

    ngOnInit() {
        if (this.data) {
            this.message = this.data.message || '';
            this.showRemoveDevicesOption = this.data.showRemoveDevicesOption || false;
        }

        this._componentSubscriptions.push(
            this.actions$.pipe(
                ofType(SessionRevokeSuccessAction),
                takeUntil(this.destroy$)
            ).subscribe(({ response }) => {
                // Show Server Response Text
                this.snackbarService.showSnackBar("success", response.Value);
                setTimeout(() => {
                    this.isActionLoading = false;
                    this.dialogRef.close();
                    location.reload();
                }, 1000);
            })
        );

        this._componentSubscriptions.push(
            this.actions$.pipe(
                ofType(SessionRevokeFailureAction),
                takeUntil(this.destroy$)
            ).subscribe(({ error }) => {
                this.isActionLoading = false;
                this.snackbarService.showSnackBar("error", "Revoking Sessions Failed.");
            })
        );
    }

    logout() {
        this.store.dispatch(LogoutAction({ skipSessionLogout: true }));
        this.dialogRef.close();
    }

    revokeSessions() {
        this.isActionLoading = true;
        this.store.dispatch(SessionRevokeAction({
            request: {
                SessionIds: [],
                RevokeAll: true
            }
        }))
    }
}

// Browser And Screen Limit Exhausted Component
@Component({
    selector: 'browser-and-screen-limit-dialog',
    standalone: true,
    template: `
        <div class="dialog-content">
            <div class="message-container">
                <div class="d-flex justify-content-center align-items-center">
                    <div class="svg-container d-flex justify-content-center align-items-center">
                        <svg xmlns="http://www.w3.org/2000/svg" width="50" height="50" viewBox="0 0 24 24" fill="none" 
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" 
                        stroke-linejoin="round" class="lucide lucide-triangle-alert h-12 w-12 text-amber-600" aria-hidden="true">
                            <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"></path>
                            <path d="M12 9v4"></path><path d="M12 17h.01"></path>
                        </svg>
                    </div>
                </div>

                <div *ngIf="!showRemoveDevicesOption" class="secondary-message">
                    Please contact the subscription owner to revoke all device connections.
                </div>
                 <div *ngIf="!showRemoveDevicesOption" class="d-flex justify-content-center align-items-center">
                    <button class="dialog-action" (click)="logout()">Close</button>
                </div>

                <div *ngIf="showRemoveDevicesOption" class="revoke-option">
                    <span>You've reached the maximum browser limit for your account. Remove an existing browser session to continue here.</span>
                </div>
                <div *ngIf="showRemoveDevicesOption" class="d-flex justify-content-center align-items-center">
                    <button class="dialog-action"  [ngClass]="{ 'disabled-btn': isActionLoading }"   (click)="revokeAll()">{{isActionLoading ? 'Loading...' : 'Continue Here'}}</button>
                </div>
            </div>
        </div>
    `,
    styles: [`
      :host {
          display: block;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
      }
      .svg-container {
        align-items: center;
        background-color: #fef3c6;
        border-radius: 50%;
        height: 70px;
        width: 70px;
      }
      .dialog-content {
          text-align: center;
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          padding: 20px;
          border-radius: 8px;
          background-color: #ffffff;
          color: #333333;
          box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
      }
      .message-container {
          display: flex;
          flex-direction: column;
          gap: 16px;
      }
      .dialog-message {
          font-size: 18px;
          font-weight: 600;
          line-height: 1.4;
      }
      .secondary-message {
          font-size: 16px;
          font-weight: 400;
          color: #555555;
      }
      .revoke-option {
          display: flex;
          flex-direction: column;
          gap: 12px;
      }
      .revoke-option span {
          font-size: 16px;
          font-weight: 400;
          color: #555555;
      }
      .dialog-action {
          background-color: red;
          color: #ffffff;
          border: none;
          padding: 10px 20px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 16px;
          font-weight: 500;
          align-self: flex-start;
          transition: background-color 0.2s ease, transform 0.1s ease;
      }
      .dialog-action:active {
          transform: translateY(0);
      }
      .dialog-close {
          background: transparent;
          border: none;
          color: #333333;
          font-size: 20px;
          font-weight: 600;
          cursor: pointer;
          transition: color 0.2s ease, transform 0.1s ease;
      }
      .dialog-close:hover {
          color: #777777;
          transform: scale(1.1);
      }
      .dialog-close:active {
          transform: scale(1);
      }
      button.disabled-btn {
        cursor: not-allowed;
        opacity: 0.4;
      }
     
    `],
    imports: [CommonModule]
})
export class BrowserAndScreenLimitDialogComponent implements OnInit {
    @Input() message: string = '';
    @Input() showRemoveDevicesOption: boolean = false;
    isActionLoading: boolean = false;

    constructor(
        private snackbarService: SnackBarService,
        private dialogRef: MatDialogRef<BrowserAndScreenLimitDialogComponent>, private actions$: Actions, private store: Store,
        @Inject(MAT_DIALOG_DATA) public data: { message: string, showRemoveDevicesOption: boolean }
    ) { }

    ngOnInit() {
        if (this.data) {
            this.message = this.data.message || '';
            this.showRemoveDevicesOption = this.data.showRemoveDevicesOption || false;
        }
    }

    logout() {
        this.store.dispatch(LogoutAction({ skipSessionLogout: true }));
        this.dialogRef.close();
    }

    revokeAll() {
        this.isActionLoading = true;
        // Dispatch the Session Revoke Action
        this.store.dispatch(SessionRevokeAction({
            request: {
                SessionIds: [],
                RevokeAll: true
            }
        }));

        // Dispatch the Session Remove Devices Action
        this.store.dispatch(SessionRemoveDevicesAction({
            request: {
                IsWeb: true,
                RemoveAll: true,
                DeviceIds: []
            }
        }));

        // Check When Both the Actions Are Success
        combineLatest([
            this.actions$.pipe(ofType(SessionRevokeSuccessAction), take(1)),
            this.actions$.pipe(ofType(SessionRemoveDevicesSuccessAction), take(1))
        ]).subscribe(([revokeResponse, removeDevicesResponse]) => {
            this.snackbarService.showSnackBar("success", "All sessions and devices revoked successfully.");
            setTimeout(() => {
                this.isActionLoading = false;
                this.dialogRef.close();
                location.reload();
            }, 1000);
        });

        // When Revoking Session Fails
        this.actions$.pipe(
            ofType(SessionRevokeFailureAction),
            take(1)
        ).subscribe(({ error }) => {
            this.snackbarService.showSnackBar("error", "Failed to revoke sessions.");
            setTimeout(() => {
                this.isActionLoading = false;
                this.dialogRef.close();
                location.reload();
            }, 1000);
        });

        // When Removing Devices Fails
        this.actions$.pipe(
            ofType(SessionRemoveDevicesFailureAction),
            take(1)
        ).subscribe(({ error }) => {
            this.snackbarService.showSnackBar("error", "Failed to remove devices.");
            setTimeout(() => {
                this.isActionLoading = false;
                this.dialogRef.close();
                location.reload();
            }, 1000);
        });
    }
}